import type { ActivationPendingOrganizationResponse } from '@acme/api/admin';
import { OrganizationService as AdminOrganizationService } from '@acme/api/admin';
import { OrganizationsTable } from '@acme/features';
import { useModal } from '@acme/foundation';
import { Alert } from 'flowbite-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function OrganizationsList() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<
    ActivationPendingOrganizationResponse[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { openConfirmModal } = useModal();

  const fetchOrganizations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response =
        await AdminOrganizationService.getApiAdminV1OrganizationPending();

      if (response.data) {
        setOrganizations(response.data);
      } else {
        setOrganizations([]);
      }
    } catch (error) {
      console.error('Ошибка загрузки организаций:', error);
      setError('Ошибка загрузки организаций. Попробуйте обновить страницу.');
      setOrganizations([]);
    } finally {
      setIsLoading(false);
    }
  }, [AdminOrganizationService]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleRowClick = (organizationId: string) => {
    navigate(`/organizations/${organizationId}`);
  };

  const handleApprove = async (organizationId: string) => {
    try {
      const confirmed = await openConfirmModal({
        title: 'Подтверждение активации',
        content: 'Вы уверены, что хотите активировать эту организацию?',
        okBtn: 'Активировать',
        cancelBtn: 'Отмена',
      });

      if (!confirmed) {
        return;
      }

      await AdminOrganizationService.putApiAdminV1OrganizationActivate({
        id: organizationId,
      });
      await fetchOrganizations();
    } catch (error) {
      console.error('Ошибка при одобрении организации:', error);
      setError('Ошибка при активации организации. Попробуйте еще раз.');
    }
  };

  const handleReject = async (organizationId: string) => {
    try {
      const confirmed = await openConfirmModal({
        title: 'Отправка на доработку',
        content:
          'Вы уверены, что хотите отправить эту организацию на доработку?',
        okBtn: 'Отправить на доработку',
        cancelBtn: 'Отмена',
      });

      if (!confirmed) {
        return;
      }

      await AdminOrganizationService.putApiAdminV1OrganizationRework({
        id: organizationId,
      });
      await fetchOrganizations();
    } catch (error) {
      console.error('Ошибка при отклонении организации:', error);
      setError('Ошибка при отправке на доработку. Попробуйте еще раз.');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-gray-900">
          Организации ожидающие подтверждения
        </h1>
      </div>

      {error && (
        <Alert color="failure">
          <span className="font-medium">Ошибка!</span> {error}
        </Alert>
      )}

      <OrganizationsTable
        organizations={organizations}
        loading={isLoading}
        onRowClick={handleRowClick}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
}
